import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "components/button";
import { toast } from "react-toastify";

interface IShipmentLine {
  heritagePartNumber?: string;
  lineNumber?: string;
  quantityShipped?: string | number;
  heritageUOM?: string;
  salesPrice?: string | number;
}

interface IShipment {
  shipmentNumber?: string | null;
  shippedDate?: string | null;
  estimatedDeliveryDate?: string | null;
  shipmentStatus?: string | null;
  shippingMethod?: string | null;
  trackingNumber?: string | null;
  shipmentDetails?: IShipmentLine[];
}

interface IOrderStatus {
  orderId?: number | string | null;
  customerOrderId?: string | null;
  transactionId?: string | null;
  heritageOrderId?: number | string | null;
  statusCode?: string | null;
  orderStatus?: string | null;
  statusDescription?: string | null;
  branchCode?: string | null;
  branchName?: string | null;
}

type StatusDetailsProps = {
  orderId: number;
  /** When provided, parent can show a compact badge without opening the modal. */
  onStatusSummary?: (summary: string | null) => void;
};

const StatusDetails = ({ orderId, onStatusSummary }: StatusDetailsProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState<IOrderStatus | null>(null);
  const [shipments, setShipments] = useState<IShipment[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [available, setAvailable] = useState(false);

  const applyResponse = (data: any) => {
    const nextStatus = data?.orderStatus ?? null;
    const nextShipments = Array.isArray(data?.shipments) ? data.shipments : [];
    setOrderStatus(nextStatus);
    setShipments(nextShipments);
    setAvailable(Boolean(data?.available));
    setMessage(data?.message ?? null);

    const summary =
      nextStatus?.statusDescription ||
      nextStatus?.orderStatus ||
      (nextStatus?.statusCode != null ? `Status ${nextStatus.statusCode}` : null);
    onStatusSummary?.(summary);
  };

  const fetchStatus = async (opts?: { silent?: boolean }) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/remote-status`,
        { params: { orderId } }
      );

      applyResponse(res.data);

      if (!opts?.silent) {
        if (res.data?.available) {
          toast.success("Order status refreshed");
        } else if (res.data?.message) {
          toast.info(res.data.message);
        } else {
          toast.info("No Heritage status available");
        }
      }
    } catch (err) {
      console.error("Failed to fetch status", err);
      if (!opts?.silent) {
        toast.error("Failed to fetch order status");
      }
      onStatusSummary?.(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchStatus({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (open) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Button className="ml-4" onClick={() => setOpen(true)}>
        Status
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Order Status Details</h2>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {orderStatus && (
              <div className="mb-4 space-y-1 text-sm text-gray-700">
                {orderStatus.statusDescription && (
                  <div>
                    <strong>Status:</strong> {orderStatus.statusDescription}
                    {orderStatus.statusCode != null
                      ? ` (${orderStatus.statusCode})`
                      : ""}
                  </div>
                )}
                {orderStatus.customerOrderId && (
                  <div>
                    <strong>Customer Order ID:</strong> {orderStatus.customerOrderId}
                  </div>
                )}
                {(orderStatus.heritageOrderId || orderStatus.orderId) && (
                  <div>
                    <strong>Heritage Order ID:</strong>{" "}
                    {orderStatus.heritageOrderId || orderStatus.orderId}
                  </div>
                )}
                {orderStatus.transactionId && (
                  <div>
                    <strong>Transaction ID:</strong> {orderStatus.transactionId}
                  </div>
                )}
                {(orderStatus.branchName || orderStatus.branchCode) && (
                  <div>
                    <strong>Branch:</strong>{" "}
                    {orderStatus.branchName || orderStatus.branchCode}
                    {orderStatus.branchName && orderStatus.branchCode
                      ? ` (${orderStatus.branchCode})`
                      : ""}
                  </div>
                )}
              </div>
            )}

            {message && (
              <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                {message}
              </div>
            )}

            <div className="mb-4">
              <Button onClick={() => fetchStatus()} disabled={loading}>
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>

            {loading && !orderStatus && shipments.length === 0 ? (
              <div className="text-sm text-gray-500">Loading order status...</div>
            ) : (
              <>
                {shipments.length > 0 ? (
                  <div className="space-y-4">
                    {shipments.map((shipment, index) => (
                      <div
                        key={shipment.shipmentNumber || index}
                        className="bg-gray-50 border rounded p-4 text-sm space-y-1"
                      >
                        <div className="font-semibold text-gray-800">
                          Shipment {shipment.shipmentNumber || `#${index + 1}`}
                        </div>
                        <div>
                          <strong>Shipment Status:</strong>{" "}
                          {shipment.shipmentStatus ?? "N/A"}
                        </div>
                        <div>
                          <strong>Shipped:</strong> {shipment.shippedDate ?? "N/A"}
                        </div>
                        <div>
                          <strong>Est. Delivery:</strong>{" "}
                          {shipment.estimatedDeliveryDate ?? "N/A"}
                        </div>
                        {shipment.shippingMethod && (
                          <div>
                            <strong>Method:</strong> {shipment.shippingMethod}
                          </div>
                        )}
                        {shipment.trackingNumber && (
                          <div>
                            <strong>Tracking #:</strong> {shipment.trackingNumber}
                          </div>
                        )}

                        {Array.isArray(shipment.shipmentDetails) &&
                          shipment.shipmentDetails.length > 0 && (
                            <table className="min-w-full bg-white text-sm border rounded mt-3">
                              <thead>
                                <tr>
                                  <th className="text-left py-2 px-3 border-b text-gray-500">
                                    Line
                                  </th>
                                  <th className="text-left py-2 px-3 border-b text-gray-500">
                                    Part #
                                  </th>
                                  <th className="text-left py-2 px-3 border-b text-gray-500">
                                    Qty
                                  </th>
                                  <th className="text-left py-2 px-3 border-b text-gray-500">
                                    UOM
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {shipment.shipmentDetails.map((line, lineIndex) => (
                                  <tr key={`${line.lineNumber}-${lineIndex}`}>
                                    <td className="py-2 px-3 border-b">
                                      {line.lineNumber ?? "—"}
                                    </td>
                                    <td className="py-2 px-3 border-b">
                                      {line.heritagePartNumber ?? "—"}
                                    </td>
                                    <td className="py-2 px-3 border-b">
                                      {line.quantityShipped ?? "—"}
                                    </td>
                                    <td className="py-2 px-3 border-b">
                                      {line.heritageUOM ?? "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                      </div>
                    ))}
                  </div>
                ) : available && orderStatus ? (
                  <div className="text-sm text-gray-500">
                    No shipment details yet for this order.
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    No status updates available.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StatusDetails;
